<template>
  <BravadoTheme forced-theme="dark" class="gong-animation">
    <div class="gong-animation__close" data-test="gong-animation__close" @click="$modal.hide('gong-animation-modal')">
      <CloseIcon width="24" height="24" />
    </div>
    <div class="gong-animation__animation">
      <VueLottie v-if="mounted" :options="options" :height="600" :width="320" @animCreated="onAnimCreated" />
    </div>
    <div class="gong-animation__overlay" data-test="gong-animation__overlay">
      <transition name="fade">
        <div v-if="animationFinished && deal" class="gong-animation__header" data-test="gong-animation__header">
          <BravadoText
            v-if="deal.company"
            tag="h5"
            variant="header5"
            class="gong-animation__company"
            data-test="gong-animation__company"
          >
            {{ deal.company.name }}
          </BravadoText>
          <BravadoText tag="h2" variant="header2" class="gong-animation__amount" data-test="gong-animation__amount">
            ${{ dealAmount }}
          </BravadoText>
          <div class="gong-animation__closed-tag">Closed</div>
        </div>
      </transition>
      <transition name="fade">
        <div v-if="animationFinished" class="gong-animation__footer" data-test="gong-animation__footer">
          <div>
            <BravadoButtonV3
              v-if="dealLink"
              variant="secondary"
              size="sm"
              :to="dealLink"
              data-test="gong-animation__view-deal-story"
            >
              View the deal story
            </BravadoButtonV3>
          </div>
          <div>
            <BravadoButtonV3
              size="sm"
              variant="blank"
              :to="newDealLink"
              data-test="gong-animation__add-one-more-deal-story"
            >
              Add one more deal
            </BravadoButtonV3>
          </div>
        </div>
      </transition>
    </div>
  </BravadoTheme>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapGetters } from 'vuex';
import type { Location } from 'vue-router';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import CloseIcon from '~/assets/svg/close.vue.svg';
import type { IDeal } from '~/types/community';
import { numberToDelimited } from '~/components/utils/core/number';
import { categoryNames } from '~/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import BravadoTheme from '~/components/common/BravadoTheme/BravadoTheme.vue';

export default Vue.extend({
  name: 'GongAnimation',
  components: { BravadoText, BravadoButtonV3, BravadoTheme, CloseIcon },
  data() {
    return {
      mounted: false,
      animationFinished: false,
      options: {
        animationData: null,
        autoplay: true,
        loop: false,
        renderer: 'svg',
      },
    };
  },
  computed: {
    ...mapGetters('deals', { dealsList: 'deals' }),
    deal(): IDeal | undefined {
      return (this.dealsList as IDeal[]).find(d => d.id === Number(this.$route.query['deal-id']));
    },
    dealLink(): string {
      return this.deal?.post ? `${this.$pages.POSTS_PAGE.path}/${this.deal!.post!.slug}` : '';
    },
    dealAmount(): string {
      return this.deal?.amount ? numberToDelimited(this.deal?.amount) : '';
    },
    newDealLink(): Location {
      return { name: this.$pages.WAR_ROOM_POSTS_NEW_PAGE.name, query: { category: categoryNames.WIN_WIRE } };
    },
  },
  mounted() {
    setTimeout(() => {
      // @ts-ignore types for vue-lottie
      Promise.all([import('vue-lottie'), import('./assets/animation.json')]).then(([VueLottie, animationData]) => {
        this.mounted = true;
        // @ts-ignore json is not assignable to null
        this.options.animationData = animationData.default;
        // @ts-ignore Object is possibly 'undefined'
        this.$options.components.VueLottie = VueLottie.default;
      });
    }, 250);
  },
  methods: {
    onAnimCreated(animation: { addEventListener: (event: string, cb: () => void) => void }) {
      animation.addEventListener('complete', () => {
        this.animationFinished = true;
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.gong-animation {
  background: rgba(var(--black));
  width: 320px;
  height: 600px;
  overflow: hidden;
  margin: 0 auto;
  border-radius: 25px;
  padding: 63px 26px 23px;
  text-align: center;
  position: relative;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    color: rgba(var(--white));
    cursor: pointer;
    z-index: 1;
  }

  &__animation {
    position: absolute;
    top: 0;
    left: 0;
    transform: scale(1.5);
  }

  &__overlay {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    position: relative;
    z-index: 1;
  }

  &__footer {
    display: grid;
    grid-row-gap: 15px;
  }

  &__company,
  &__amount {
    color: rgba(var(--white));
  }

  &__closed-tag {
    display: inline-block;
    padding: 8px 16px;
    margin-top: 12px;
    background: var(--gold-50);
    border-radius: 16px;
    transform: rotate(-9.1deg);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 2s ease-out;
  }

  .fade-enter,
  .fade-leave {
    opacity: 0;
  }
}
</style>
